/**
 * PaymentsColdStart
 *
 * Cold start onboarding experience for Payments feature.
 * Shows when Stripe Connect is not yet set up.
 *
 * Used in two locations:
 * 1. PaymentsPage (/practice/payments) - full page variant
 * 2. PatientDetailView Payments tab - embedded variant
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Check as CheckIcon, FiberManualRecord as BulletIcon } from '@mui/icons-material';

import { useEventTracking } from '@services/statsig';
import { useGroupUserRoles } from '@features/user';
import { hasBillingAccess } from '@utils/roleUtils';

import { useStripeConnect } from '../store/hooks';
import { StripeConnectRedirect } from './StripeConnectRedirect';

export interface PaymentsColdStartProps {
  /** Variant affects layout - 'page' for full page, 'embedded' for within tabs */
  variant?: 'page' | 'embedded';
}

const COMMON_CHARGES = [
  'Session fees (self-pay / out-of-network)',
  'Copays and patient balances',
  'Admin fees (FMLA, letters, forms)',
  'No-show / late cancellation fees',
];

const BENEFITS = [
  'Powered by Stripe',
  'Control your own pricing',
  'Deposits go to your bank account',
];

export const PaymentsColdStart: React.FC<PaymentsColdStartProps> = ({ variant = 'page' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { startOnboarding, isCreating } = useStripeConnect();
  const { stripeConnect } = useEventTracking();
  const groupUserRoles = useGroupUserRoles();

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);

  // Check if user can manage billing (practice_administrator or billing_administrator)
  const canManageBilling = hasBillingAccess(groupUserRoles);

  const handleGetStarted = async () => {
    // For embedded variant (patient page), navigate to practice payments page
    if (variant === 'embedded') {
      navigate('/practice/payments');
      return;
    }

    // For page variant, start Stripe onboarding directly
    try {
      setIsRedirecting(true);
      stripeConnect.initiated({
        source: 'payments_page_cold_start',
      });
      const result = await startOnboarding();
      if (result.onboarding_url) {
        setOnboardingUrl(result.onboarding_url);
      }
    } catch (err) {
      console.error('Failed to start Stripe onboarding:', err);
      stripeConnect.failed({
        error_type: 'onboarding_start_error',
        error_message: err instanceof Error ? err.message : 'Unknown error',
      });
      setIsRedirecting(false);
    }
  };

  const handleRedirect = () => {
    if (onboardingUrl) {
      window.location.href = onboardingUrl;
    }
  };

  // Show redirect countdown UI
  if (isRedirecting && onboardingUrl) {
    return (
      <Box sx={{ py: variant === 'page' ? 4 : 2 }}>
        <StripeConnectRedirect
          variant="inline"
          onCountdownComplete={handleRedirect}
          redirectDelay={5}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          Payments
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            mb: 2,
          }}
        >
          Powered by Stripe &middot; Easy setup in minutes
        </Typography>
        <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
          Collect patient payments for anything you bill outside insurance—especially out-of-network
          care.
        </Typography>
      </Box>

      {/* Two column layout */}
      <Grid container spacing={3}>
        {/* Left: Get started */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.text.primary,
                }}
              >
                Get started
              </Typography>

              {/* Show CTA button for admins, contact message for non-admins */}
              {canManageBilling ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetStarted}
                  disabled={isCreating || isRedirecting}
                  startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ mb: 3 }}
                  fullWidth
                >
                  {isCreating
                    ? 'Connecting...'
                    : variant === 'embedded'
                      ? 'Set up in Your Practice'
                      : 'Start getting paid'}
                </Button>
              ) : (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Contact your practice administrator to set up patient payments.
                </Alert>
              )}

              <List dense disablePadding>
                {BENEFITS.map(benefit => (
                  <ListItem key={benefit} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={benefit}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.primary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Common charges */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: theme.palette.text.primary,
                }}
              >
                Common charges
              </Typography>
              <List dense disablePadding>
                {COMMON_CHARGES.map(charge => (
                  <ListItem key={charge} disableGutters sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <BulletIcon sx={{ fontSize: 8, color: theme.palette.text.secondary }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={charge}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.primary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PaymentsColdStart;
